import { Select } from "@components";
import { useMemo, useState } from "react";
import { TRAILER_BODY_TYPE_IDS } from "../constants";
import { useGetAllVehicles } from "../query";
import type { VehicleAllListItem } from "../types";

type TrailerPlateComboBoxProps = {
	excludeVehicleId: number;
	selectedVehicle: VehicleAllListItem | null;
	onSelect: (vehicle: VehicleAllListItem | null) => void;
	isInvalid?: boolean;
	isOpen?: boolean;
	assignedTrailerIds?: number[];
};

export const TrailerPlateComboBox = ({
	excludeVehicleId,
	selectedVehicle,
	onSelect,
	isInvalid,
	isOpen = true,
	assignedTrailerIds = [],
}: TrailerPlateComboBoxProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [inputValue, setInputValue] = useState(selectedVehicle?.plate ?? "");
	const { data } = useGetAllVehicles(isOpen);

	const trailerOptions = useMemo(() => {
		const vehicles = data ?? [];
		const assignedIds = new Set(assignedTrailerIds);
		return vehicles.filter(
			(vehicle) =>
				vehicle.id !== excludeVehicleId &&
				!assignedIds.has(vehicle.id) &&
				TRAILER_BODY_TYPE_IDS.has(Number(vehicle.bodyType))
		);
	}, [data, excludeVehicleId, assignedTrailerIds]);

	const filteredOptions = useMemo(() => {
		const query = searchTerm.trim().toLowerCase();
		if (!query) {
			return trailerOptions;
		}
		return trailerOptions.filter((vehicle) =>
			vehicle.plate.toLowerCase().includes(query)
		);
	}, [trailerOptions, searchTerm]);

	const selectItems = useMemo(
		() =>
			filteredOptions.map((vehicle) => ({
				id: vehicle.id,
				label: vehicle.plate,
			})),
		[filteredOptions]
	);

	const handleInputChange = (value: string) => {
		setInputValue(value);
		setSearchTerm(value);
	};

	return (
		<Select.ComboBox
			allowsEmptyCollection
			aria-label="Buscar reboque ou semirreboque por placa"
			className="w-full"
			inputValue={inputValue}
			isInvalid={isInvalid}
			items={selectItems}
			onInputChange={handleInputChange}
			onSelectionChange={(key) => {
				const vehicle =
					filteredOptions.find((item) => String(item.id) === String(key)) ??
					null;
				setInputValue(vehicle?.plate ?? "");
				onSelect(vehicle);
			}}
			placeholder="Ex: AAA1A11"
			selectedKey={selectedVehicle ? String(selectedVehicle.id) : null}
			shortcut={false}
		>
			{(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
		</Select.ComboBox>
	);
};
